import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "../../ui/UIElements/Icon";
import { useGlobalUtils } from "../../../lib/globalUtils";
import { useTranslation } from "react-i18next";
import {
  STATUS_LABELS,
  STATUS_BADGE_COLORS,
  getStatusGroup,
} from "@finitless/shared/orderConstants";
import { useOrderDetail } from "../../../hooks/useOrderDetail";
import { useOrderSocketUpdates } from "../../../hooks/useOrderSocketUpdates";
import Breadcrumb from "../../ui/Navigation/Breadcrumb";
import ConversationAiModal from "../../ui/Modals/ConversationAiModal";

const OrderDetailPage = ({ initialOrder = null }) => {
  const { t } = useTranslation("orders");
  const { id } = useParams();
  const orderId = parseInt(id);
  const navigate = useNavigate();
  const location = useLocation();
  const [showConversationModal, setShowConversationModal] = useState(false);
  const { parseDate, parsePrice, calculateEstimatedDelivery } =
    useGlobalUtils();

  useOrderSocketUpdates();

  // Get order data from navigation state or props
  const orderFromState = location.state?.order;
  const orderData = initialOrder || orderFromState;

  // Fetch order data using custom hook
  const {
    data: order,
    isLoading,
    error,
    isError,
  } = useOrderDetail(orderId, orderData);

  // Helper functions for included taxes
  const getIncludedTaxes = (isDeliveryFee = false) => {
    if (!order) return 0;
    if (order?.taxes?.length === 0) {
      return order?.tax_type === 1 ? (order?.summary?.tax ?? 0) : 0;
    } else {
      return order?.taxes?.reduce((taxIncluded, tax) => {
        return (
          taxIncluded +
          ((!isDeliveryFee && tax.type === 1 && tax.target === "product") ||
          (isDeliveryFee && tax.type === 1 && tax.target === "delivery_fee")
            ? tax.summary?.tax
            : 0)
        );
      }, 0);
    }
  };

  const getIncludedTaxesDiscounts = () => {
    if (!order) return 0;
    return (
      order?.taxes
        ?.filter((tax) => tax?.type === 1 && tax?.target === "product")
        ?.reduce(
          (carry, tax) =>
            carry + (tax?.summary?.tax_after_discount ?? tax?.summary?.tax),
          0,
        ) ?? 0
    );
  };

  const handleBackToOrders = () => {
    navigate("/orders");
  };

  const handleViewConversation = () => {
    setShowConversationModal(true);
  };

  const handleCloseConversationModal = () => {
    setShowConversationModal(false);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-500"></div>
          <p className="text-gray-400">{t("detail.states.loading")}</p>
        </div>
      </div>
    );
  }

  if (isError || (!isLoading && !order)) {
    return (
      <div className="py-12 text-center">
        <Icon
          name="ShoppingBag"
          className="mx-auto mb-4 h-12 w-12 text-gray-400"
        />
        <h3 className="mt-2 text-sm font-medium text-gray-300">
          {t("detail.states.notFoundTitle")}
        </h3>
        <p className="mt-1 text-sm text-gray-400">
          {error?.message || t("detail.states.notFoundDescription")}
        </p>
        <button
          onClick={handleBackToOrders}
          className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
        >
          {t("detail.actions.backToOrders")}
        </button>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const statusGroup = getStatusGroup(status);
    const statusLabel = t(`statusLabels.${status}`, {
      defaultValue: STATUS_LABELS[status] || status,
    });

    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
          STATUS_BADGE_COLORS[statusGroup] || STATUS_BADGE_COLORS.unknown
        }`}
      >
        {statusLabel}
      </span>
    );
  };

  const breadcrumbItems = [
    { label: t("header.title"), path: "/orders", icon: "ShoppingBag" },
    {
      label: order
        ? t("card.orderNumber", { id: order.id })
        : t("detail.states.loadingShort"),
      active: true,
      disabled: true,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <div></div>
        <div className="flex space-x-3">
          <button
            onClick={handleViewConversation}
            className="flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
          >
            <Icon name="MessageCircle" className="h-4 w-4" />
            <span>{t("detail.actions.viewConversation")}</span>
          </button>
        </div>
      </div>

      {/* Order Header */}
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">
              {t("card.orderNumber", { id: order?.id })}
            </h1>
            <p className="text-gray-400">{t("detail.header.orderDetails")}</p>
          </div>
          <div className="text-right">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-3xl font-bold text-transparent">
              {parsePrice(order.summary?.total) || "0.00"}
            </div>
            <p className="text-sm text-gray-400">
              {t("detail.labels.totalAmount")}
            </p>
          </div>
        </div>

        {/* Order Status */}
        <div className="flex items-center justify-between">
          <span className="text-gray-400">{t("detail.labels.status")}:</span>
          {getStatusBadge(order.status)}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Customer Information */}
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
            <Icon name="User" className="mr-2 h-5 w-5" />
            {t("detail.sections.customerInformation")}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">{t("detail.labels.name")}:</span>
              <span className="text-white">
                {order.customer?.name} {order.customer?.lastname}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">{t("detail.labels.phone")}:</span>
              <span className="text-white">
                {order.customer?.country_phone_code
                  ? `+${order.customer.country_phone_code}`
                  : ""}{" "}
                {order.customer?.cellphone}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">{t("detail.labels.email")}:</span>
              <span className="text-white">
                {order.customer?.email || t("common.notAvailable")}
              </span>
            </div>
          </div>
        </div>

        {/* Delivery Information */}
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
            <Icon name="Truck" className="mr-2 h-5 w-5" />
            {t("detail.sections.deliveryInformation")}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">
                {t("detail.labels.address")}:
              </span>
              <span className="text-right text-white">
                {order?.customer?.address || t("common.notAvailable")}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">
                {t("detail.labels.orderTime")}:
              </span>
              <span className="text-white">
                {parseDate(order?.created_at, { useUTC: true })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">
                {t("detail.labels.estimatedDelivery")}:
              </span>
              <span className="text-white">
                {parseDate(calculateEstimatedDelivery(order))}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">
                {t("detail.labels.paymentMethod")}:
              </span>
              <span className="text-white">
                {order.paymethod?.name || t("common.notAvailable")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Order Items */}
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
        <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
          <Icon name="ShoppingBag" className="mr-2 h-5 w-5" />
          {t("detail.sections.orderItems")}
        </h3>
        <div className="space-y-3">
          {(order.products || []).map((product, index) => (
            <div
              key={product.id || index}
              className="rounded-lg bg-gray-800 p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-white">
                    {t("detail.labels.productWithQuantity", {
                      quantity: product.quantity,
                      name: product.name,
                    })}
                  </h4>
                  {product.comment && (
                    <p className="mt-1 text-sm text-gray-400">
                      {t("detail.labels.note")}: {product.comment}
                    </p>
                  )}
                  {/* Show options if available */}
                  {product.options && product.options.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {product.options.map((option, optIndex) => (
                        <div key={optIndex} className="text-sm text-gray-300">
                          <span className="text-gray-400">{option.name}:</span>
                          {option.suboptions?.map((suboption, subIndex) => (
                            <span key={subIndex} className="ml-1">
                              {suboption.name}
                              {suboption.price > 0 &&
                                ` (+${parsePrice(suboption.price)})`}
                              {subIndex < option.suboptions.length - 1 && ", "}
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="ml-4 text-right">
                  <div className="font-medium text-white">
                    {parsePrice(product.total || product.summary?.total || 0)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="mt-6 space-y-2 border-t border-gray-600 pt-4">
          <div className="flex justify-between">
            <span className="text-gray-400">
              {t("detail.summary.subtotal")}:
            </span>
            <span className="text-white">
              {parsePrice(
                (order?.summary?.subtotal ?? order?.subtotal ?? 0) +
                  getIncludedTaxes(),
              )}
            </span>
          </div>
          {(order?.summary?.discount > 0 || order?.discount > 0) &&
            !order?.offers?.length && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-400">
                    {order?.offer_type === 1 && order?.offer_rate ? (
                      <>
                        {t("detail.summary.discount")} ({order.offer_rate}%)
                      </>
                    ) : (
                      <>{t("detail.summary.discount")}</>
                    )}
                    :
                  </span>
                  <span className="text-green-400">
                    -
                    {parsePrice(
                      order?.summary?.discount ?? order?.discount ?? 0,
                    )}
                  </span>
                </div>
                {order?.summary?.subtotal_with_discount > 0 &&
                  order?.summary?.discount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">
                        {t("detail.summary.subtotalAfterDiscount")}:
                      </span>
                      <span className="text-white">
                        $
                        {parsePrice(
                          order?.tax_type === 1
                            ? order.summary.subtotal_with_discount +
                                getIncludedTaxesDiscounts()
                            : order.summary.subtotal_with_discount,
                        )}
                      </span>
                    </div>
                  )}
              </>
            )}
          {order?.offers?.length > 0 &&
            order?.offers
              ?.filter((offer) => offer?.target === 1)
              ?.map((offer) => (
                <React.Fragment key={offer.id}>
                  <div className="flex justify-between">
                    <span className="text-gray-400">
                      {offer.name}
                      {offer.rate_type === 1 && offer?.rate && (
                        <span className="text-gray-500"> ({offer.rate}%)</span>
                      )}
                      :
                    </span>
                    <span className="text-green-400">
                      -{parsePrice(offer?.summary?.discount ?? 0)}
                    </span>
                  </div>
                </React.Fragment>
              ))}
          {order?.taxes?.length === 0 &&
            order?.tax_type === 2 &&
            order?.summary?.tax > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-400">
                  {t("detail.summary.tax")}
                  {order?.tax && (
                    <span className="text-gray-500"> ({order.tax}%)</span>
                  )}
                  :
                </span>
                <span className="text-white">
                  {parsePrice(order.summary.tax)}
                </span>
              </div>
            )}
          {order?.fees?.length === 0 && order?.summary?.service_fee > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-400">
                {t("detail.summary.serviceFee")}
                {order?.service_fee && (
                  <span className="text-gray-500"> ({order.service_fee}%)</span>
                )}
                :
              </span>
              <span className="text-white">
                {parsePrice(order.summary.service_fee)}
              </span>
            </div>
          )}
          {order?.taxes?.length > 0 &&
            order?.taxes
              ?.filter(
                (tax) =>
                  tax?.type === 2 &&
                  tax?.rate !== 0 &&
                  tax?.target === "product",
              )
              .map((tax) => (
                <div key={tax?.id} className="flex justify-between">
                  <span className="text-gray-400">
                    {tax?.name || t("detail.summary.tax")}
                    {tax?.rate && (
                      <span className="text-gray-500"> ({tax.rate}%)</span>
                    )}
                    :
                  </span>
                  <span className="text-white">
                    {parsePrice(
                      tax?.summary?.tax_after_discount ??
                        tax?.summary?.tax ??
                        0,
                    )}
                  </span>
                </div>
              ))}
          {order?.fees?.length > 0 &&
            order?.fees
              ?.filter((fee) => !(fee?.fixed === 0 && fee?.percentage === 0))
              ?.map((fee) => (
                <div key={fee.id} className="flex justify-between">
                  <span className="text-gray-400">
                    {fee?.name || t("detail.summary.fee")}
                    {(fee?.fixed > 0 || fee?.percentage > 0) && (
                      <span className="text-gray-500">
                        {" "}
                        (
                        {fee?.fixed > 0 &&
                          `$${fee.fixed.toFixed(2)}${fee.percentage > 0 ? " + " : ""}`}
                        {fee.percentage > 0 && `${fee.percentage}%`})
                      </span>
                    )}
                    :
                  </span>
                  <span className="text-white">
                    {parsePrice(
                      (fee?.summary?.fixed ?? 0) +
                        (fee?.summary?.percentage_after_discount ??
                          fee?.summary?.percentage ??
                          0),
                    )}
                  </span>
                </div>
              ))}
          {order?.offers?.length > 0 &&
            order?.offers
              ?.filter((offer) => offer?.target === 3)
              ?.map((offer) => (
                <div key={offer.id} className="flex justify-between">
                  <span className="text-gray-400">
                    {offer.name}
                    {offer.rate_type === 1 && offer?.rate && (
                      <span className="text-gray-500"> ({offer.rate}%)</span>
                    )}
                    :
                  </span>
                  <span className="text-green-400">
                    -{parsePrice(offer?.summary?.discount ?? 0)}
                  </span>
                </div>
              ))}
          {order?.summary?.delivery_price !== undefined &&
            order?.summary?.delivery_price !== null &&
            order?.delivery_type !== 2 && (
              <div className="flex justify-between">
                <span className="text-gray-400">
                  {t("detail.summary.deliveryFee")}:
                </span>
                <span className="text-white">
                  {parsePrice(
                    order.summary.delivery_price + getIncludedTaxes(true),
                  )}
                </span>
              </div>
            )}
          {order?.extra_value_checkprice &&
            order?.extra_value_checkprice > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-400">
                  {t("detail.summary.extraValueCheckprice")}:
                </span>
                <span className="text-white">
                  {parsePrice(order.extra_value_checkprice)}
                </span>
              </div>
            )}
          {order?.taxes?.length > 0 &&
            order?.taxes
              ?.filter(
                (tax) =>
                  tax?.type === 2 &&
                  tax?.rate !== 0 &&
                  tax?.target === "delivery_fee",
              )
              .map((tax) => (
                <div key={tax?.id} className="flex justify-between">
                  <span className="text-gray-400">
                    {tax?.name || t("detail.summary.tax")}
                    {tax?.rate && (
                      <span className="text-gray-500"> ({tax.rate}%)</span>
                    )}
                    :
                  </span>
                  <span className="text-white">
                    {parsePrice(
                      tax?.summary?.tax_after_discount ??
                        tax?.summary?.tax ??
                        0,
                    )}
                  </span>
                </div>
              ))}
          {order?.offers?.length > 0 &&
            order?.offers
              ?.filter((offer) => offer?.target === 2)
              ?.map((offer) => (
                <div key={offer.id} className="flex justify-between">
                  <span className="text-gray-400">
                    {offer.name}
                    {offer.rate_type === 1 && offer?.rate && (
                      <span className="text-gray-500"> ({offer.rate}%)</span>
                    )}
                    :
                  </span>
                  <span className="text-green-400">
                    -{parsePrice(offer?.summary?.discount ?? 0)}
                  </span>
                </div>
              ))}
          {(order?.summary?.driver_tip > 0 || order?.driver_tip > 0) && (
            <div className="flex justify-between">
              <span className="text-gray-400">
                {t("detail.summary.driverTip")}
                {order?.driver_tip_rate && (
                  <span className="text-gray-500">
                    {" "}
                    ({order.driver_tip_rate}%)
                  </span>
                )}
                :
              </span>
              <span className="text-white">
                {parsePrice(
                  order?.summary?.driver_tip ?? order?.totalDriverTip ?? 0,
                )}
              </span>
            </div>
          )}
          {order?.metafields
            ?.filter(
              (meta) =>
                meta?.key?.includes("extra_value") &&
                ![0, -0].includes(parseFloat(meta?.value)),
            )
            ?.map((extra, index) => (
              <div key={index} className="flex justify-between">
                <span className="text-gray-400">{extra?.key}:</span>
                <span className="text-white">
                  {parseFloat(extra?.value) > 0
                    ? parsePrice(parseFloat(extra.value))
                    : `-${parsePrice(parseFloat(extra.value) * -1)}`}
                </span>
              </div>
            ))}
          <div className="flex justify-between border-t border-gray-600 pt-2 text-xl font-bold">
            <span className="text-white">{t("detail.summary.total")}:</span>
            <span className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
              {parsePrice(order?.summary?.total ?? 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Special Instructions */}
      {order.comment && (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
            <Icon name="FileText" className="mr-2 h-5 w-5" />
            {t("detail.sections.specialInstructions")}
          </h3>
          <div className="rounded-lg bg-gray-800 p-4">
            <p className="text-gray-300">{order.comment}</p>
          </div>
        </div>
      )}

      {/* Business Information */}
      {order.business && (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
            <Icon name="Building" className="mr-2 h-5 w-5" />
            {t("detail.sections.businessInformation")}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">
                {t("detail.labels.businessName")}:
              </span>
              <span className="text-white">{order.business.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">
                {t("detail.labels.address")}:
              </span>
              <span className="text-right text-white">
                {order.business.address}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">{t("detail.labels.phone")}:</span>
              <span className="text-white">
                {order.business.cellphone || order.business.phone}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Conversation Modal */}
      <ConversationAiModal
        order={order}
        isOpen={showConversationModal}
        onClose={handleCloseConversationModal}
      />
    </div>
  );
};

export default OrderDetailPage;
